import { RoleType, ENDORSEMENT_VALIDATION, getUserInitials } from '@promo/shared';
import type { Endorsement, UpdateEndorsementDTO } from '@promo/shared';
import { InviteEndorserDialog } from './InviteEndorserDialog';
import { InvitationsList } from './InvitationsList';
import { useInvitations } from '../hooks/useInvitations';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useEndorsements } from '../hooks/useEndorsements';
import { EndorsementForm } from './EndorsementForm';
import { Skeleton } from '@/components/ui/skeleton';
import { UserInfo } from '@/components/common/UserInfo';
import { useUser } from '@/hooks/useUser';
import { getInitials } from '@promo/shared';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { parseNameFromEmail } from '@promo/shared';

interface EndorsementsSectionProps {
  packetId: string;
  readOnly?: boolean;
  isEditor?: boolean;
  isCandidate?: boolean;
}

// Component to render a single endorsement item with stable hook calls
interface EndorsementItemProps {
  item: Endorsement;
  currentUserId: string;
  isEditable: boolean;
  isManager: boolean;
  updateEndorsement: (id: string, data: UpdateEndorsementDTO) => void;
  isUpdating: boolean;
  deleteEndorsement: (id: string) => void;
  isDeleting: boolean;
}
function EndorsementItem({
  item,
  currentUserId,
  isEditable,
  isManager,
  updateEndorsement,
  isUpdating,
  deleteEndorsement,
  isDeleting,
}: EndorsementItemProps) {
  const { data: user } = useUser(item.endorserId);
  const initials = user
    ? getUserInitials(user)
    : getInitials(`User ${item.endorserId.substring(0, 5)}...`, undefined);
  const name = user?.name ?? `User ${item.endorserId.substring(0, 5)}...`;
  const canModify = isEditable && (item.endorserId === currentUserId || isManager);

  // For own endorsement, show editable title; for others, show title or email as fallback
  const isOwnEndorsement = item.endorserId === currentUserId;
  const showEditableTitle = (isOwnEndorsement || isManager) && !!user;

  return (
    <div className="not-first:py-6 pb-10">
      {/* Endorsement form */}
      <div className="mb-4">
        <EndorsementForm
          initialContent={item.content}
          onSubmit={canModify ? (content) => updateEndorsement(item.id, { content }) : () => {}}
          onCancel={() => {}}
          onDelete={canModify ? () => deleteEndorsement(item.id) : undefined}
          isSubmitting={isUpdating || isDeleting}
          readOnly={!canModify}
        />
      </div>

      {/* User info row with responsive grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        {/* Cell 1: Content area (hidden on mobile, 2/3 on desktop) */}
        <div className="hidden md:block md:col-span-3">
          {/* This space can be used for additional content if needed */}
        </div>

        {/* Cell 2: User info (full width on mobile, 1/3 on desktop) */}
        <div className="col-span-4 md:col-span-1 flex justify-start">
          <UserInfo
            initials={initials}
            name={name}
            userId={user?.id}
            currentTitle={user?.title}
            editableTitle={showEditableTitle}
          />
        </div>
      </div>
    </div>
  );
}

export function EndorsementsSection({
  packetId,
  readOnly,
  isEditor = false,
  isCandidate = false,
}: EndorsementsSectionProps) {
  const { session } = useAuth();
  const currentUserId = session.user?.id ?? '';
  const currentUserEmail = session.user?.email?.toLowerCase();
  const isEditable = !readOnly;
  // Load invitations for editors and invited endorsers.
  const { data: existingInvitations = [] } = useInvitations(packetId, RoleType.ENDORSER);
  const canCreateOnBehalf = isEditor || isCandidate;

  // Editor override: selected endorser
  const [selectedEndorserId, setSelectedEndorserId] = useState<string>('');

  // Hook drives all data operations
  const {
    endorsements,
    isLoadingEndorsements,
    endorsementsError,
    createEndorsement,
    isCreatingEndorsement,
    updateEndorsement,
    isUpdatingEndorsement,
    deleteEndorsement,
    isDeletingEndorsement,
  } = useEndorsements(packetId);

  // Build dropdown options for editors (all invites, excluding those who already endorsed)
  const availableEndorsers: SelectOption[] = existingInvitations
    .filter((inv) => !endorsements.some((e) => e.endorserId === inv.inviteeId))
    .map((inv) => ({
      value: inv.inviteeId,
      label: parseNameFromEmail(inv.inviteeEmail) || inv.inviteeEmail,
    }));

  // Manage new/add form
  const [newFormKey, setNewFormKey] = useState(0);

  // Find current user's endorsement (if any)
  const userEndorsement = endorsements.find((e) => e.endorserId === currentUserId);
  const currentUserInvitation = existingInvitations.find(
    (inv) =>
      inv.inviteeId === currentUserId ||
      (currentUserEmail !== undefined && inv.inviteeEmail.toLowerCase() === currentUserEmail)
  );
  const canCreateOwnEndorsement = !canCreateOnBehalf && !!currentUserInvitation && !userEndorsement;
  const canRenderOnBehalfForm = canCreateOnBehalf && availableEndorsers.length > 0;
  const shouldRenderCreateForm = isEditable && (canCreateOwnEndorsement || canRenderOnBehalfForm);
  const shouldRenderEndorsementForm = !canCreateOnBehalf || selectedEndorserId !== '';

  // Loading state
  if (isLoadingEndorsements) {
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Endorsements</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading endorsements...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (endorsementsError) {
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Endorsements</h2>
        </div>
        <div className="bg-red-50 text-red-800 p-4 rounded-md">
          <h3 className="text-lg font-semibold">Error loading endorsements</h3>
          <p>{endorsementsError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4">
      <div>
        {/* Header with badge */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-semibold">Endorsements</h2>
            <Badge>
              {endorsements.length} of {ENDORSEMENT_VALIDATION.MAX_ENDORSERS}
            </Badge>
          </div>
        </div>
        <div>
          <p className="mb-10">
            Collect endorsements from peers with the same Target Title or above to support the
            promotion of the candidate. Gather detailed examples from peers to highlight how the
            candidate met the criteria for promotion.
          </p>
        </div>

        {/* Empty state: show skeleton placeholder when no endorsements */}
        {!isLoadingEndorsements && endorsements.length === 0 && (
          <div className="space-y-4">
            <Skeleton className="h-4 w-3/4" />
            <div className="flex items-center space-x-3 pt-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          </div>
        )}

        {/* Existing endorsements */}
        {endorsements.map((item) => (
          <EndorsementItem
            key={item.id}
            item={item}
            currentUserId={currentUserId}
            isEditable={isEditable}
            isManager={isEditor}
            updateEndorsement={updateEndorsement}
            isUpdating={isUpdatingEndorsement}
            deleteEndorsement={deleteEndorsement}
            isDeleting={isDeletingEndorsement}
          />
        ))}

        {/* New endorsement form (for invited endorsers and editors/candidate) */}
        {shouldRenderCreateForm && (
          <div className="py-6 space-y-4 border-b last:border-b-0">
            {canCreateOnBehalf && (
              <EditableSelect
                options={availableEndorsers}
                value={selectedEndorserId}
                onValueChange={setSelectedEndorserId}
                placeholder="Select endorser"
                disabled={isCreatingEndorsement}
              />
            )}
            {shouldRenderEndorsementForm && (
              <EndorsementForm
                key={newFormKey}
                onSubmit={(content) =>
                  createEndorsement(
                    {
                      packetId,
                      content,
                      ...(canCreateOnBehalf ? { endorserId: selectedEndorserId } : {}),
                    },
                    {
                      onSuccess: () => {
                        setNewFormKey((k) => k + 1);
                        setSelectedEndorserId('');
                      },
                    }
                  )
                }
                onCancel={() => setNewFormKey((k) => k + 1)}
                isSubmitting={isCreatingEndorsement}
                startInEditMode
              />
            )}
          </div>
        )}
      </div>

      {/* Editor or candidate can invite endorsers */}
      {(isEditor || isCandidate) && (
        <div className="my-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium">Invitations</h3>
              <Badge variant="secondary">
                {existingInvitations.length} of {ENDORSEMENT_VALIDATION.MAX_ENDORSERS}
              </Badge>
            </div>
            <InviteEndorserDialog packetId={packetId} />
          </div>
          <InvitationsList packetId={packetId} role={RoleType.ENDORSER} />
        </div>
      )}
    </div>
  );
}
